// @ts-strict-ignore
import { AfterViewInit, Component, ElementRef, Input, OnInit, Signal, TemplateRef, viewChild, ViewContainerRef } from '@angular/core';

@Component({
  selector: 'tooltip',
  templateUrl: './tooltip.component.html',
})
export class TooltipComponent implements OnInit, AfterViewInit {
  private static readonly GAP = 10;

  @Input() content: string | TemplateRef<any>;
  @Input() placement: string;
  @Input() top: number;
  @Input() left: number;
  @Input() maxWidth;
  @Input() textAlign: string;
  @Input() autoVisible = true;
  @Input() maxCharsNowrap = 30;

  /** Make sure to sanitize any external user-provided text when setting this to true */
  @Input() allowHtml = false;

  private contentElementRef: Signal<ElementRef> = viewChild(`content`);
  private contentViewContainerRef: Signal<ViewContainerRef> = viewChild(`templateRef` , { read: ViewContainerRef });

  text: string;

  private contentLeft: number;
  private contentTop: number;
  private contentWidth: number;
  private contentHeight: number;

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    const el = this.contentElementRef()?.nativeElement;
    el.style.top = 0;
    el.style.left = 0;
    if (typeof this.content === `string`) {
      this.updateTextContent(this.content);
      el.style.maxWidth = this.maxWidth ? `${this.maxWidth}px` : `300px`;
    } else if (!this.content) {
      // do nothing for empty tooltip content
    } else {
      el.classList.add(`text-nowrap`);
      el.classList.add(`text-nowrap-inner`);
      this.contentViewContainerRef()?.createEmbeddedView(this.content);
    }

    el.style.padding = `{TooltipComponent.GAP}px`;
    this.makeTransparent();
  }

  public updateTextContent(newText: string): void {
    if (typeof this.content === `string`) {
      const el = this.contentElementRef()?.nativeElement;
      el.style.removeProperty('top');
      el.style.removeProperty('left');
      el.style.removeProperty('width');
      this.content = newText;
      this.text = newText;
      if (this.maxCharsNowrap && this.text.indexOf(`<br`) === -1 && this.text.length <= this.maxCharsNowrap) {
        el.classList.add(`text-nowrap`);
      } else {
        el.classList.remove(`text-nowrap`);
      }
      if(this.allowHtml) {
        el.innerHTML = this.text;
      } else {
        el.innerText = this.text;
      }
      this.positionContent();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.positionContent();
      if (this.autoVisible) {
        this.makeVisible();
      }
    }, 0);
  }

  public positionContent(): void {
    const el = this.contentElementRef()?.nativeElement;
    let contentWidth = el.clientWidth;
    let contentHeight = el.clientHeight;

    while (contentWidth / contentHeight < 1.3) {
      el.style.width = `${contentWidth + 10}px`;
      contentWidth = el.clientWidth;
      contentHeight = el.clientHeight;
    }

    let contentTop = 0;
    let contentLeft = 0;

    // center content
    switch (this.placement) {
      case `top`:
        contentTop = this.top - contentHeight - TooltipComponent.GAP;
        contentLeft = this.left - contentWidth / 2;
        break;
      case `bottom`:
        contentTop = this.top + TooltipComponent.GAP;
        contentLeft = this.left - contentWidth / 2;
        break;
      case `bottom-right`:
        contentTop = this.top + TooltipComponent.GAP;
        contentLeft = this.left;
        break;
      case `left`:
        contentTop = this.top - contentHeight / 2;
        contentLeft = this.left - contentWidth - TooltipComponent.GAP;
        break;
      case `right`:
        contentTop = this.top - contentHeight / 2;
        contentLeft = this.left + TooltipComponent.GAP;
        break;
    }

    // adjust position on left and right screen sides
    if (this.placement === `top` || this.placement === `bottom` || this.placement === `bottom-right`) {
      if (contentLeft < 0) {
        contentLeft = TooltipComponent.GAP;
      }
      const windowWidth = document.body.clientWidth;
      if (contentLeft + contentWidth > windowWidth) {
        contentLeft = windowWidth - contentWidth - TooltipComponent.GAP;
      }
    }

    el.style.top = `${contentTop}px`;
    el.style.left = `${contentLeft}px`;

    this.contentLeft = contentLeft;
    this.contentTop = contentTop;
    this.contentWidth = contentWidth + 10;
    this.contentHeight = contentHeight;
  }

  public makeTransparent(): void {
    this.elementRef.nativeElement.style.visibility = `hidden`;
  }

  public makeVisible(): void {
    if (this.content) {
      this.elementRef.nativeElement.style.visibility = `visible`;
    }
  }

  public isVisible(): boolean {
    return this.elementRef.nativeElement.style.visibility !== `hidden`;
  }

  public containsClientPosition(clientX: number, clientY: number): boolean {
    return (
      clientX >= this.contentLeft &&
      clientX <= this.contentLeft + this.contentWidth &&
      clientY >= this.contentTop &&
      clientY <= this.contentTop + this.contentHeight
    );
  }
}
